import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import {
  getRangeEndSuffix,
  getRangeStartSuffix,
} from "@lib/helpers/IntlMessages";
import { createYupSync } from "@lib/helpers/TableHelper";
import { walletAddressSchema as yupSchema } from "@lib/schemas/wallet";
import { PaginationType, UseQueryProps } from "@lib/types/Api";
import { Wallet, WalletAddressCreationType } from "@lib/types/Billing";
import {
  SendByteWallet,
  SendByteWalletAddressCreationType,
} from "@lib/types/SendByte";
import { Button, Form, InputNumber, Typography } from "antd";
import { FormInstance } from "antd/lib";
import { useEffect, useMemo, useState } from "react";
import { LuMousePointerClick } from "react-icons/lu";
import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl";

type Props = {
  walletsQuery: UseQueryProps<PaginationType<Wallet | SendByteWallet>>;
  creationType: WalletAddressCreationType | SendByteWalletAddressCreationType;
  form: FormInstance<any>;
  loading: boolean;
};

export default function WalletAddressCustomIdInput(props: Props) {
  const { walletsQuery, creationType, form, loading } = props;
  const walletId = Form.useWatch("walletId", form);
  const customId = Form.useWatch("customId", form);
  const address = Form.useWatch("address", form);
  const { messages, locale } = useIntl();
  const [suffix, setSuffix] = useState<null | JSX.Element>(null);
  const addresses = address
    ?.trim()
    ?.split("\n")
    .filter((x) => !!x);
  const startId = Number(customId || 0);
  const endId = addresses?.length ? startId + (addresses?.length - 1 || 0) : 0;

  useEffect(() => {
    if (
      creationType == WalletAddressCreationType.MULTIPLE &&
      addresses?.length &&
      !!startId &&
      !!endId
    ) {
      const values = {
        startId,
        endId,
        startSuffix: getRangeStartSuffix(locale, startId),
        endSuffix: getRangeEndSuffix(locale, endId),
      };
      setSuffix(
        <small>
          <FormattedMessage id="common.formatRange" values={values} />
        </small>,
      );
    } else {
      setSuffix(null);
    }
  }, [creationType, startId, endId]);

  const lastCustomId = useMemo(() => {
    if (walletId) {
      return (
        walletsQuery?.data?.data?.data?.find((d) => d.id == walletId)
          ?.lastCustomId + 1
      );
    }
    return null;
  }, [walletId, customId, walletsQuery?.data?.data]);

  const handleChangeValue = () => {
    form?.setFieldValue("customId", lastCustomId);
  };

  return (
    <Form.Item
      name="customId"
      rules={[createYupSync({ schema: yupSchema })]}
      label={messages["common.walletAddressCustomId"] as string}
      help={
        <>
          {!customId && lastCustomId && (
            <StyledFlex style={{ gap: "10px", marginBottom: "15px" }}>
              <Typography>
                {messages["common.nextAddressNumber"] as string}:{" "}
                <b>{lastCustomId}</b>
              </Typography>
              <Button
                size="small"
                icon={<LuMousePointerClick />}
                onClick={handleChangeValue}
                type="primary"
              >
                {messages["common.apply"] as string}
              </Button>
            </StyledFlex>
          )}
        </>
      }
    >
      <InputNumber
        disabled={loading || !walletId}
        suffix={suffix}
        style={{ width: suffix ? "250px" : "150px" }}
        type="number"
      />
    </Form.Item>
  );
}
